import React from 'react';
import styled from '@emotion/styled';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Grid, GridCell } from './Grid';
import { Breadcrumbs } from './Breadcrumb';
import Headline3 from './Typography/Heading/Headline3';
import grey from './Palettes/grey';
import overlay from './Palettes/overlay';
import colors from './Theme/colors';

const JobsContainer = styled.div`
  margin: 0px auto;
  padding: 24px;
  max-width: 970px;
  box-sizing: border-box;
  color: #FFF;
  z-index: 3;
`;

const JobsHeader = styled.div`
  margin: 0 auto;
  padding: 24px 0;
  text-align: center;
`;

const JobsHeadline = styled(Headline3)`
  && {
    margin-bottom: 16px;
    padding: 0 16px;
  }
`;

const JobsMain = styled(Grid)`
  && a {
    text-decoration: none;
  }
  && {
    margin: 0;
    padding: 0;
    width: 100%;
  }
  color: ${colors.black};
  background-color: ${colors.white};
  line-height: 1.8;
  font-size: 108%;
  border: 1px solid ${grey[325]};
  && button {
    background: ${({ theme }) => theme.palette.primary.main};
    color: white;
    border: 2px solid ${({ theme }) => theme.palette.primary.main};
    text-align: center;
    margin-left: 4px;
    white-space: nowrap;
  }
`;

const JobsFooter = styled.div`
  color: ${colors.black};
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.sizes.body1};
  padding: 4px 0 0 4px;
`;

const JobsBackground = styled.div`
  background-color: #f5f5f5;
  width: 100vw;
  min-height: 100vh;
  position: fixed;
  z-index: -1;
  top: 0;
  left: 0;
`;

const JobApplyBreadcrumbs = styled(Breadcrumbs)`
  padding-left: 4px;
  margin-bottom: 0;
  height: 24px;
  :before {
    display: none;
  }
`;

export const JobApplyFormControlLabel = styled(FormControlLabel)`
  &.MuiFormControlLabel-root {
    margin: 0;
    display: inline;
  }

  .MuiFormControlLabel-label {
    color: ${overlay[600]};
    font-family: ${({ theme }) => theme.fonts.primary};
    transform: none;
    letter-spacing: unset;
  }

  .MuiButtonBase-root {
    padding: 3px;
    display: inline;
  }

  .MuiTypography-root {
    position: relative;
    bottom: 3px;
  }
`;

export const JobApplyContainer = ({
  children,
  title,
  backButton,
  breadcrumbLinks,
}: {
  children?: React.ReactElement;
  title?: React.ReactNode;
  backButton?: React.ReactElement;
  breadcrumbLinks: React.ReactElement[];
}) => (
  <>
    <JobsContainer>
      {title && (
        <JobsHeader>
          <JobsHeadline tag="h1">
            {title}
          </JobsHeadline>
        </JobsHeader>
      )}
      <div>
        <JobApplyBreadcrumbs
          crumbs={breadcrumbLinks}
        />
      </div>
      <JobsMain spacing={10}>
        {children}
      </JobsMain>
      <JobsFooter>
        {backButton}
      </JobsFooter>
    </JobsContainer>
    <JobsBackground />
  </>
);

const JobFormPanel = styled(GridCell)`
  && {
    padding: 40px 40px 20px;
  }

  background-color: #FFF;

  .form-item {
    margin-bottom: 24px;
  }

  .form-item > div {
    width: 100%
  }
`;

const JobInfoPanel = styled(GridCell)`
  && {
    padding: 40px;
  }

  border-left: 1px solid ${grey[325]};
  color: ${colors.black};
  font-size: ${({ theme }) => theme.sizes.h5};
  font-family: ${({ theme }) => theme.fonts.secondary};
`;

const JobDetailList = styled.ul`
  padding: 0;
  margin: 8px 0 0;
`;

const JobDetailListItem = styled.li`
  list-style-type: none;
  padding: 0;
  margin-bottom: 0;
  color: ${grey[650]};
`;

const JobDetailLabel = styled.span`
  width: 33%;
  display: inline-block;
`;

const JobDetailValue = styled.span`
  width: 67%;
  display: inline-block;
`;

const PrivacyDisclaimer = styled.p`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.sizes.body2};
  line-height: 1.25rem;
  letter-spacing: .01471rem;
  color: ${overlay[600]};

  && a {
    color: ${grey[650]};
  }
`;

const JobApply = ({
  children,
  jobDetails,
  privacyDisclaimer,
}: {
  children: React.ReactElement;
  jobDetails: Record<string, string>[];
  privacyDisclaimer: string;
}) => (
  <>
    <JobFormPanel xs={12} md={6}>
      {children}
    </JobFormPanel>
    <JobInfoPanel xs={12} md={6}>
      <JobDetailList>
        {
          jobDetails.map(
            ({ label, value }) => (
              <JobDetailListItem key={label}>
                <JobDetailLabel>
                  {label}
                </JobDetailLabel>
                <JobDetailValue>
                  {value}
                </JobDetailValue>
              </JobDetailListItem>
            ),
          )
        }
      </JobDetailList>
      <hr />
      <PrivacyDisclaimer
        dangerouslySetInnerHTML={
          { __html: privacyDisclaimer }
        }
      />
    </JobInfoPanel>
  </>
);

export default JobApply;
